import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Trophy, Hammer, Megaphone } from "lucide-react";

function initialsOf(name) {
  if (!name) return "??";
  return name
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

const MEDAL_COLORS = [
  "bg-[var(--mp-yellow)] text-[var(--mp-ink)] border-[var(--mp-ink)]", // 🥇
  "bg-white text-[var(--mp-ink)] border-[var(--mp-ink)]", // 🥈
  "bg-[var(--mp-primary)] text-white border-[var(--mp-ink)]", // 🥉
];

export default function MvpLeaderboard({ days = 7 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/staff/leaderboard`, { params: { days } });
      setItems(data.items || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) return null;

  return (
    <div
      data-testid="mvp-leaderboard"
      className="rounded-3xl border border-[var(--mp-ink)] bg-white overflow-hidden shadow-brutal-sm"
    >
      <div className="flex items-center justify-between gap-4 px-6 py-4 bg-[var(--mp-ink)] text-white">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-[var(--mp-yellow)] text-[var(--mp-ink)] grid place-items-center">
            <Trophy className="h-4 w-4" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-lg tracking-tight leading-none">
              MVP della settimana
            </div>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/60 mt-1">
              Ultimi {days} giorni · +2 pt lavorato · +1 pt pubblicato
            </div>
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="p-8 text-center text-[var(--mp-muted)]">
          Nessuna attività negli ultimi {days} giorni. Appena qualcuno lavora o pubblica un
          contenuto, comparirà qui.
        </div>
      ) : (
        <ul className="divide-y divide-[var(--mp-border)]">
          {items.map((u, idx) => {
            const medal = MEDAL_COLORS[idx];
            return (
              <li
                key={u.user_id}
                data-testid={`mvp-row-${idx}`}
                className="flex items-center gap-4 px-6 py-3.5"
              >
                {medal ? (
                  <div
                    className={`h-10 w-10 rounded-full grid place-items-center font-display text-sm border ${medal}`}
                  >
                    {idx + 1}
                  </div>
                ) : (
                  <div className="h-10 w-10 rounded-full grid place-items-center font-display text-sm bg-[var(--mp-dash-bg)] text-[var(--mp-muted)]">
                    {idx + 1}
                  </div>
                )}
                <div className="h-10 w-10 rounded-full bg-[var(--mp-dash-bg)] grid place-items-center font-display text-sm">
                  {initialsOf(u.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base tracking-tight truncate">
                    {u.name}
                  </div>
                  <div className="flex items-center gap-4 text-[11px] uppercase tracking-[0.16em] text-[var(--mp-muted)] mt-0.5">
                    <span className="inline-flex items-center gap-1">
                      <Hammer className="h-3 w-3" /> {u.edited} lavorati
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Megaphone className="h-3 w-3" /> {u.published} pubblicati
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl tracking-tighter leading-none">
                    {u.points}
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--mp-muted)] mt-1">
                    punti
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
